/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import { styles } from '../ReportsScreen.styles';
import { spacing } from '../../../shared/theme/spacing';
import { ReportsDenseListCard } from './ReportsDenseListCard';
import { ReportsBlockHeading } from './ReportsBlockHeading';
import { usePlanEntitlements } from '../../../shell/providers/PlanEntitlementsProvider';
import { canonicalComplaintReason, complaintReasonLabel, fetchComplaintStats, type ComplaintStats } from '../../../complaints/public-api';

export function ComplaintsStatsSection({ from, to }: { from: string; to: string }) {
  const { entitlements } = usePlanEntitlements();
  const orgId = entitlements?.organization_id;
  const [stats, setStats] = useState<ComplaintStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    setError(false);
    fetchComplaintStats(orgId, from, to)
      .then((data) => setStats(data))
      .catch((e) => {
        console.warn('ComplaintsStatsSection', e);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [orgId, from, to]);

  if (!orgId) return null;

  const resolvedRate = stats && stats.total > 0
    ? Math.round((stats.resolved / stats.total) * 100)
    : 0;
  const escalatedRate = stats && stats.total > 0
    ? Math.round((stats.escalated / stats.total) * 100)
    : 0;

  return (
    <View style={{ marginTop: spacing.md }}>
      <ReportsBlockHeading label="Reclamos online" />
      <ReportsDenseListCard>
        {loading ? (
          <Text style={styles.caption}>Cargando…</Text>
        ) : stats && stats.total > 0 ? (
          <>
            <View style={styles.reportsDenseListFieldRow}>
              <Text style={styles.reportsDenseListFieldLabel}>Total reclamos</Text>
              <Text style={styles.reportsDenseListFieldValue}>{stats.total}</Text>
            </View>
            <View style={styles.reportsDenseListFieldRow}>
              <Text style={styles.reportsDenseListFieldLabel}>Resueltos</Text>
              <Text style={styles.reportsDenseListFieldValue}>{stats.resolved} ({resolvedRate}%)</Text>
            </View>
            <View style={styles.reportsDenseListFieldRow}>
              <Text style={styles.reportsDenseListFieldLabel}>Escalados</Text>
              <Text style={styles.reportsDenseListFieldValue}>{stats.escalated} ({escalatedRate}%)</Text>
            </View>
            <View style={[styles.reportsDenseListFieldRow, { borderTopWidth: 1, borderTopColor: '#E0E0E0', paddingTop: spacing.xs, marginTop: spacing.xs }]}>
              <Text style={styles.reportsDenseListFieldLabel}>Por motivo</Text>
            </View>
            {(() => {
              // Motivos consolidados a nivel display (delay + delivery_delay = 'Demora').
              const merged = new Map<keyof typeof stats.byReason, number>();
              for (const [reason, count] of Object.entries(stats.byReason)) {
                const canonical = canonicalComplaintReason(reason as keyof typeof stats.byReason);
                merged.set(canonical, (merged.get(canonical) ?? 0) + count);
              }
              return [...merged.entries()].map(([reason, count]) => {
                if (count === 0) return null;
                return (
                  <View key={reason} style={styles.reportsDenseListFieldRow}>
                    <Text style={styles.reportsDenseListFieldLabel}>{complaintReasonLabel(reason)}</Text>
                    <Text style={styles.reportsDenseListFieldValue}>{count}</Text>
                  </View>
                );
              });
            })()}
          </>
        ) : error ? (
          <Text style={styles.caption}>No se pudieron cargar las estadísticas.</Text>
        ) : (
          <Text style={styles.caption}>Sin reclamos en el período.</Text>
        )}
      </ReportsDenseListCard>
    </View>
  );
}
